import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
    Package, Clock, Hash, Tag, ExternalLink,
    ShoppingCart, Link2, ChevronDown, ChevronUp,
    Pencil
} from 'lucide-react';

import InboxHeader       from '../../components/Inbox/InboxHeader';
import ActionButtons     from '../../components/Inbox/ActionButtons';
import RemarksHistory    from '../../components/Inbox/RemarksHistory';
import InboxSplitLayout  from '../../components/Inbox/InboxSplitLayout';
import VerificationInput from '../../components/Inbox/VerificationInput';

import {
    fetchItemCodeInbox,
    fetchItemCodeDetail,
    fetchItemCodeTraders,
    fetchItemCodeLinks,
    fetchItemCodeRemarks,
    submitItemCodeVerification,
    clearDetail,
    clearApprovalResult,
    resetAll,
    selectItemCodeInbox,
    selectItemCodeDetail,
    selectItemCodeTraders,
    selectItemCodeLinks,
    selectItemCodeRemarks,
    selectICLoading,
    selectICErrors,
} from '../../slices/purchaseSlice/itemCodeVerificationSlice';

import {
    fetchStatusList,
    selectEnabledActions,
    selectHasActions,
    selectStatusListLoading,
    selectStatusListError,
    resetApprovalData,
    setShowReturnButton,
} from '../../slices/CommonSlice/getStatusSlice';

// ── Component ─────────────────────────────────────────────────────────────────

const VerifyItemCode = ({ notificationData, onNavigate }) => {
    const dispatch = useDispatch();

    const inbox          = useSelector(selectItemCodeInbox);
    const detail         = useSelector(selectItemCodeDetail);
    const traders        = useSelector(selectItemCodeTraders);
    const links          = useSelector(selectItemCodeLinks);
    const remarks        = useSelector(selectItemCodeRemarks);
    const loading        = useSelector(selectICLoading);
    const errors         = useSelector(selectICErrors);

    const statusLoading  = useSelector(selectStatusListLoading);
    const statusError    = useSelector(selectStatusListError);
    const enabledActions = useSelector(selectEnabledActions);
    const hasActions     = useSelector(selectHasActions);

    const { userData, userDetails } = useSelector((s) => s.auth);
    const roleId   = userData?.roleId || userData?.RID;
    const userName = userData?.userName || userDetails?.userName || 'system';

    // Local UI state
    const [selectedItem,         setSelectedItem]         = useState(null);
    const [isVerified,           setIsVerified]           = useState(false);
    const [verificationComment,  setVerificationComment]  = useState('');
    const [showRemarksHistory,   setShowRemarksHistory]   = useState(false);
    const [searchQuery,          setSearchQuery]          = useState('');
    const [isLeftPanelCollapsed, setIsLeftPanelCollapsed] = useState(false);
    const [isLeftPanelHovered,   setIsLeftPanelHovered]   = useState(false);
    const [showTraders,          setShowTraders]          = useState(false);
    const [showLinks,            setShowLinks]            = useState(false);

    // Editable fields — verifier can update these
    const [editBasicprice,   setEditBasicprice]   = useState('');
    const [editItemname,     setEditItemname]     = useState('');
    const [editSpecification, setEditSpecification] = useState('');

    const { InboxTitle, ModuleDisplayName } = notificationData || {};

    const getCurrentRoleName = () =>
        userDetails?.roleName || userData?.roleName ||
        notificationData?.InboxTitle || 'Item Code Verifier';

    // ── Lifecycle ─────────────────────────────────────────────────────────────

    useEffect(() => {
        if (roleId) dispatch(fetchItemCodeInbox(roleId));
        dispatch(setShowReturnButton('Yes'));
        return () => {
            dispatch(resetAll());
            dispatch(resetApprovalData());
        };
    }, [roleId, dispatch]);

    // Fetch detail when item selected
    useEffect(() => {
        if (!selectedItem) return;
        dispatch(fetchItemCodeDetail(selectedItem.Rowid));
        setIsVerified(false);
        setVerificationComment('');
        setShowRemarksHistory(false);
        setShowTraders(false);
        setShowLinks(false);
        dispatch(clearDetail());
    }, [selectedItem, dispatch]);

    // After detail loads — fetch supplementary data and populate editable fields
    useEffect(() => {
        if (!detail || !roleId) return;

        const moid  = detail.MOID || 270;
        const tranNo = detail.TranNo || '';

        dispatch(fetchStatusList({ MOID: moid, ROID: roleId, ChkAmt: 0 }));

        if (tranNo) {
            dispatch(fetchItemCodeTraders(tranNo));
            dispatch(fetchItemCodeLinks(tranNo));
            dispatch(fetchItemCodeRemarks(tranNo));
        }

        setEditBasicprice(detail.Basicprice || '');
        setEditItemname(detail.Itemname || '');
        setEditSpecification(detail.Specification || '');
    }, [detail, roleId, dispatch]);

    // Collapse left panel when item selected
    useEffect(() => {
        if (selectedItem) setIsLeftPanelCollapsed(true);
    }, [selectedItem]);

    // ── Handlers ──────────────────────────────────────────────────────────────

    const handleRefresh = () => {
        if (roleId) dispatch(fetchItemCodeInbox(roleId));
    };

    const handleBackToInbox = () => {
        if (onNavigate) onNavigate('dashboard', { name: 'Dashboard', type: 'dashboard' });
    };

    const buildPayload = (actionValue) => {
        const roleName = getCurrentRoleName();
        const existingRemarks = detail?.Remarks || '';
        const formatted = `${roleName} : ${userName} : ${verificationComment.trim()}`;
        const updatedRemarks = existingRemarks.trim()
            ? `${existingRemarks.trim()}||${formatted}`
            : formatted;

        return {
            Rowid:         detail?.Rowid || selectedItem?.Rowid || '',
            Remarks:       verificationComment.trim(), // SP builds the 'Action : Role : User : note |' history itself; legacy sends the note only,
            Appstatus:     actionValue,
            RoleID:        roleId,
            LastRoleID:    detail?.LastRoleID || null,
            Basicprice:    editBasicprice,
            Itemname:      editItemname,
            Specification: editSpecification,
            Createdby:     userName,
        };
    };

    const handleActionClick = async (action) => {
        if (!selectedItem) { toast.error('No item code selected.'); return; }
        if (!verificationComment.trim()) {
            toast.error('Verification comment is mandatory.');
            return;
        }
        if (!isVerified) {
            toast.error('Please check the verification checkbox before proceeding.');
            return;
        }

        let actionValue = action.value || action.text || action.type;
        if (!actionValue?.trim()) {
            const map = { approve: 'Approve', verify: 'Verify', reject: 'Reject', return: 'Return' };
            actionValue = map[action.type?.toLowerCase()] || 'Verify';
        }

        try {
            const result = await dispatch(submitItemCodeVerification(buildPayload(actionValue))).unwrap();
            // spVerifyItemCode returns 'Submitted' or an error text (HTTP 200 either way).
            const statusText = String(result?.Data ?? result ?? '').trim();
            if (statusText.split('$')[0] !== 'Submitted') {
                toast.error(statusText || 'Action was not applied', { autoClose: 10000 });
                return;
            }
            const msg = typeof result === 'string' ? result : JSON.stringify(result);

            // Backend returns "Invalid ..." strings in Data even when IsSuccessful: true
            if (typeof result === 'string' && result.toLowerCase().startsWith('invalid')) {
                toast.error(result, { autoClose: 10000 });
                return;
            }

            toast.success(`${action.text || actionValue} completed successfully!`);
            if (msg.includes('$')) {
                const info = msg.split('$')[1];
                if (info) setTimeout(() => toast.info(info, { autoClose: 6000 }), 500);
            }
            setTimeout(() => {
                dispatch(fetchItemCodeInbox(roleId));
                setSelectedItem(null);
                setVerificationComment('');
                setIsVerified(false);
                setShowRemarksHistory(false);
                setIsLeftPanelCollapsed(false);
                dispatch(clearDetail());
                dispatch(resetApprovalData());
                dispatch(clearApprovalResult());
            }, 1000);
        } catch (err) {
            const msg = typeof err === 'string' ? err : err?.message || `Failed to ${actionValue.toLowerCase()}`;
            toast.error(msg, { autoClose: 10000 });
        }
    };

    // ── Filtered inbox ────────────────────────────────────────────────────────

    const filteredItems = inbox.filter((item) => {
        if (!searchQuery) return true;
        const q = searchQuery.toLowerCase();
        return (
            item.Itemname?.toLowerCase().includes(q) ||
            item.ItemCode?.toLowerCase().includes(q) ||
            item.Rowid?.toString().includes(q)
        );
    });

    // ── Left panel renderers ──────────────────────────────────────────────────

    const renderItemCard = (item) => (
        <div className="p-4">
            <div className="flex items-center gap-3 mb-3">
                <div className="relative">
                    <div className="w-11 h-11 rounded-full border-2 border-blue-200 dark:border-blue-600 bg-gradient-to-br from-blue-100 to-blue-100 dark:from-blue-800/40 dark:to-blue-800/40 flex items-center justify-center">
                        <Package className="w-5 h-5 text-blue-800 dark:text-blue-400" />
                    </div>
                    <div className="absolute -bottom-1 -right-1 w-3.5 h-3.5 bg-amber-400 rounded-full border-2 border-white dark:border-gray-800" />
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                        {item.Itemname || '—'}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate font-mono">
                        {item.ItemCode}
                    </p>
                </div>
            </div>
            <div className="flex flex-wrap gap-1 text-xs">
                {item.Basicprice && (
                    <span className="flex items-center gap-1 px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full">
                        ₹ {item.Basicprice}
                    </span>
                )}
                <span className="flex items-center gap-1 px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-full">
                    <Hash className="w-3 h-3" />{item.Rowid}
                </span>
            </div>
        </div>
    );

    // Compact single-line row for the "classic" list view — same fields as
    // renderItemCard, laid out horizontally instead of stacked.
    const renderListItem = (item) => (
        <div className="flex items-center gap-x-6 gap-y-1 flex-wrap text-sm">
            <span className="font-semibold text-gray-900 dark:text-white min-w-[200px] truncate">
                {item.Itemname || '—'}
            </span>
            <span className="font-mono text-gray-500 dark:text-gray-400 min-w-[110px]">
                {item.ItemCode}
            </span>
            {item.Basicprice && (
                <span className="px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full text-xs font-medium">
                    ₹ {item.Basicprice}
                </span>
            )}
            <span className="ml-auto flex items-center gap-1 px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-full text-xs">
                <Hash className="w-3 h-3" />{item.Rowid}
            </span>
        </div>
    );

    const renderCollapsedItem = () => (
        <div className="w-full h-full rounded-lg border-2 border-blue-200 dark:border-blue-600 bg-gradient-to-br from-blue-100 to-blue-100 dark:from-blue-800/40 dark:to-blue-800/40 flex items-center justify-center">
            <Package className="w-4 h-4 text-blue-800 dark:text-blue-400" />
        </div>
    );

    // ── Detail panel ──────────────────────────────────────────────────────────

    const Field = ({ label, value, mono }) => {
        if (!value) return null;
        return (
            <div className="flex items-start gap-2">
                <span className="text-xs text-gray-500 dark:text-gray-400 w-36 shrink-0 pt-0.5">{label}</span>
                <span className={`text-sm font-medium text-gray-900 dark:text-white ${mono ? 'font-mono tracking-wide' : ''}`}>
                    {value}
                </span>
            </div>
        );
    };

    const SectionToggle = ({ title, count, isOpen, onToggle, icon: Icon }) => (
        <button
            onClick={onToggle}
            className="flex items-center justify-between w-full px-4 py-3 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-700 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
        >
            <span className="flex items-center gap-2 text-sm font-semibold text-blue-900 dark:text-blue-300">
                <Icon className="w-4 h-4" />
                {title}
                {count > 0 && (
                    <span className="px-2 py-0.5 bg-blue-900 text-white rounded-full text-xs">{count}</span>
                )}
            </span>
            {isOpen ? <ChevronUp className="w-4 h-4 text-blue-700" /> : <ChevronDown className="w-4 h-4 text-blue-700" />}
        </button>
    );

    const renderDetailContent = (isPopup = false) => {
        if (!selectedItem) return null;

        return (
            <div className="space-y-6">
                {loading.detail && (
                    <div className="flex items-center gap-3 bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 border border-blue-200 dark:border-blue-700">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-900" />
                        <span className="text-sm text-blue-900 dark:text-blue-400">Loading item code details...</span>
                    </div>
                )}

                {/* Header card */}
                <div className="bg-gradient-to-r from-blue-50 via-blue-50 to-orange-50 dark:from-blue-900/20 dark:via-blue-900/20 dark:to-orange-900/20 rounded-2xl p-6 border-2 border-blue-200 dark:border-blue-700">
                    <div className="flex items-start gap-4">
                        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-900 to-blue-700 flex items-center justify-center shadow-lg shrink-0">
                            <Package className="w-7 h-7 text-white" />
                        </div>
                        <div className="flex-1">
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                                {detail?.Itemname || selectedItem?.Itemname}
                            </h2>
                            <p className="text-blue-800 dark:text-blue-400 font-semibold text-sm mt-0.5 font-mono">
                                {detail?.ItemCode || selectedItem?.ItemCode}
                                {detail?.TranNo && ` · Txn: ${detail.TranNo}`}
                            </p>
                            <div className="flex flex-wrap gap-2 mt-3">
                                {detail?.TransactionType && (
                                    <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-900 dark:text-blue-300 rounded-full text-xs font-medium">
                                        {detail.TransactionType}
                                    </span>
                                )}
                                {detail?.ItemCodeType && (
                                    <span className="px-3 py-1 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 rounded-full text-xs font-medium">
                                        {detail.ItemCodeType}
                                    </span>
                                )}
                                {detail?.Units && (
                                    <span className="px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-full text-xs font-medium">
                                        Unit: {detail.Units}
                                    </span>
                                )}
                                {detail?.Basicprice && (
                                    <span className="px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full text-xs font-medium">
                                        Basic: ₹ {detail.Basicprice}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Item details grid */}
                {detail && (
                    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
                        <h3 className="text-sm font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-4 flex items-center gap-2">
                            <Tag className="w-4 h-4 text-blue-800" /> Item Details
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
                            <Field label="HSN Code"       value={detail.HSNCode}         mono />
                            <Field label="HSN Remarks"    value={detail.HSNRemarks} />
                            <Field label="DCA"            value={detail.ItemcodeDca} />
                            <Field label="Sub DCA"        value={detail.ItemcodeSDca} />
                            <Field label="Major Group"    value={detail.Majorgroupcode ? `${detail.Majorgroupcode}${detail.Majorgroupname ? ' — ' + detail.Majorgroupname : ''}` : null} />
                            <Field label="Sub Group"      value={detail.Subgroupcode ? `${detail.Subgroupcode}${detail.Subgroupname ? ' — ' + detail.Subgroupname : ''}` : null} />
                            <Field label="Spec Code"      value={detail.Specificationcode} mono />
                            <Field label="Row ID"         value={detail.Rowid} mono />
                        </div>

                        {detail.Specification && (
                            <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Specification</p>
                                <p className="text-sm text-gray-800 dark:text-gray-200 leading-relaxed">
                                    {detail.Specification}
                                </p>
                            </div>
                        )}
                    </div>
                )}

                {/* Editable fields (verifier can update) */}
                {detail && (
                    <div className="bg-orange-50 dark:bg-orange-900/10 rounded-xl border border-orange-200 dark:border-orange-700 p-5">
                        <h3 className="text-sm font-bold text-orange-700 dark:text-orange-400 uppercase tracking-wide mb-4 flex items-center gap-2">
                            <Pencil className="w-4 h-4" /> Updatable Fields
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_9rem] gap-4 items-start">
                            <div>
                                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                                    Item Name
                                </label>
                                <input
                                    type="text"
                                    value={editItemname}
                                    onChange={(e) => setEditItemname(e.target.value)}
                                    className="w-full px-3 py-2 text-sm border border-orange-300 dark:border-orange-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-400"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                                    Specification
                                </label>
                                <textarea
                                    rows={3}
                                    value={editSpecification}
                                    onChange={(e) => setEditSpecification(e.target.value)}
                                    className="w-full px-3 py-2 text-sm border border-orange-300 dark:border-orange-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-400 resize-none"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                                    Basic Price
                                    {detail?.Basicprice && (
                                        <span className="ml-1 text-gray-400 font-normal">
                                            (max: ₹{detail.Basicprice})
                                        </span>
                                    )}
                                </label>
                                <input
                                    type="number"
                                    value={editBasicprice}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        const original = parseFloat(detail?.Basicprice || 0);
                                        if (val !== '' && parseFloat(val) > original) return;
                                        setEditBasicprice(val);
                                    }}
                                    max={detail?.Basicprice || undefined}
                                    min={0}
                                    className={`w-full px-3 py-2 text-sm border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-400 ${
                                        parseFloat(editBasicprice) > parseFloat(detail?.Basicprice || 0)
                                            ? 'border-red-400 dark:border-red-500'
                                            : 'border-orange-300 dark:border-orange-600'
                                    }`}
                                />
                                {parseFloat(editBasicprice) > parseFloat(detail?.Basicprice || 0) && (
                                    <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                                        Price cannot exceed the original ₹{detail.Basicprice}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Traders section */}
                {detail?.TranNo && (
                    <div className="space-y-2">
                        <SectionToggle
                            title="Trader Quotes"
                            count={traders.length}
                            isOpen={showTraders}
                            onToggle={() => setShowTraders(!showTraders)}
                            icon={ShoppingCart}
                        />
                        {showTraders && (
                            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                                {loading.traders ? (
                                    <div className="p-6 text-center text-sm text-gray-500">Loading traders...</div>
                                ) : traders.length === 0 ? (
                                    <div className="p-6 text-center text-sm text-gray-400">No trader data found.</div>
                                ) : (
                                    <table className="w-full text-sm">
                                        <thead className="bg-blue-100 dark:bg-blue-900/30">
                                            <tr>
                                                {['Supplier', 'Phone', 'Email', 'Rate', 'Amount', 'Basic'].map((h) => (
                                                    <th key={h} className="px-4 py-2 text-left text-xs font-semibold text-blue-900 dark:text-blue-200">{h}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                            {traders.map((t, i) => (
                                                <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                                                    <td className="px-4 py-2 font-medium text-gray-800 dark:text-gray-200">{t.SupName}</td>
                                                    <td className="px-4 py-2 text-gray-600 dark:text-gray-400">{t.Supphone}</td>
                                                    <td className="px-4 py-2 text-gray-600 dark:text-gray-400">{t.Supemail}</td>
                                                    <td className="px-4 py-2 text-green-700 dark:text-green-400 font-medium">₹ {t.SupRate}</td>
                                                    <td className="px-4 py-2 text-green-700 dark:text-green-400">₹ {t.SupAmt}</td>
                                                    <td className="px-4 py-2 text-blue-900 dark:text-blue-400">₹ {t.Basic}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Links section */}
                {detail?.TranNo && (
                    <div className="space-y-2">
                        <SectionToggle
                            title="Link Data"
                            count={links.length}
                            isOpen={showLinks}
                            onToggle={() => setShowLinks(!showLinks)}
                            icon={Link2}
                        />
                        {showLinks && (
                            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                                {loading.links ? (
                                    <div className="p-6 text-center text-sm text-gray-500">Loading links...</div>
                                ) : links.length === 0 ? (
                                    <div className="p-6 text-center text-sm text-gray-400">No link data found.</div>
                                ) : (
                                    <table className="w-full text-sm">
                                        <thead className="bg-blue-100 dark:bg-blue-900/30">
                                            <tr>
                                                {['Link', 'Short Link', 'Rate', 'Amount', 'Basic'].map((h) => (
                                                    <th key={h} className="px-4 py-2 text-left text-xs font-semibold text-blue-900 dark:text-blue-200">{h}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                            {links.map((lk, i) => (
                                                <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                                                    <td className="px-4 py-2 max-w-xs truncate">
                                                        {lk.Link ? (
                                                            <a href={lk.Link} target="_blank" rel="noopener noreferrer"
                                                               className="text-blue-800 dark:text-blue-400 hover:underline flex items-center gap-1">
                                                                <ExternalLink className="w-3 h-3" /> View
                                                            </a>
                                                        ) : '—'}
                                                    </td>
                                                    <td className="px-4 py-2 max-w-xs truncate">
                                                        {lk.Linkshort ? (
                                                            <a href={lk.Linkshort} target="_blank" rel="noopener noreferrer"
                                                               className="text-blue-800 dark:text-blue-400 hover:underline flex items-center gap-1">
                                                                <ExternalLink className="w-3 h-3" /> View
                                                            </a>
                                                        ) : '—'}
                                                    </td>
                                                    <td className="px-4 py-2 text-green-700 dark:text-green-400 font-medium">₹ {lk.LinkRate}</td>
                                                    <td className="px-4 py-2 text-green-700 dark:text-green-400">₹ {lk.LinkAmt}</td>
                                                    <td className="px-4 py-2 text-blue-900 dark:text-blue-400">₹ {lk.Basic}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        )}
                    </div>
                )}

                <RemarksHistory
                    isOpen={showRemarksHistory}
                    onToggle={() => setShowRemarksHistory(!showRemarksHistory)}
                    remarks={remarks}
                    loading={loading.remarks}
                    title="Approval History"
                />

                <VerificationInput
                    isVerified={isVerified}
                    onVerifiedChange={setIsVerified}
                    comment={verificationComment}
                    onCommentChange={(e) => setVerificationComment(e.target.value)}
                    config={{
                        checkboxLabel: '✓ I have verified the item code details',
                        checkboxDescription: 'Confirm that the item name, HSN code, specification and pricing are correct',
                        commentLabel: 'Verification Comments',
                        commentPlaceholder: 'Enter your verification remarks...',
                        commentRequired: true,
                        commentRows: 3,
                        commentMaxLength: 500,
                        showCharCount: true,
                        validationStyle: 'dynamic',
                        checkboxGradient: 'from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20',
                        commentGradient: 'from-blue-50 to-orange-50 dark:from-blue-900/20 dark:to-orange-900/20',
                        commentBorder: 'border-blue-200 dark:border-blue-700',
                    }}
                />

                {statusLoading ? (
                    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700 flex items-center justify-center gap-3">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-900" />
                        <span className="text-gray-600 dark:text-gray-400">Loading actions...</span>
                    </div>
                ) : statusError ? (
                    <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-5 border border-red-200 dark:border-red-700 text-center text-sm text-red-600 dark:text-red-400">
                        ⚠️ Error loading actions: {statusError}
                    </div>
                ) : !hasActions || !enabledActions?.length ? (
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-xl p-5 border border-yellow-200 dark:border-yellow-700 text-center text-sm text-yellow-700 dark:text-yellow-400">
                        ℹ️ No actions available for this record
                    </div>
                ) : (
                    <ActionButtons
                        actions={enabledActions}
                        onActionClick={handleActionClick}
                        loading={loading.submit}
                        isVerified={isVerified}
                        comment={verificationComment}
                        showValidation={true}
                        excludeActions={['send back']}
                    />
                )}
            </div>
        );
    };


    // ── Main render ───────────────────────────────────────────────────────────

    return (
        <div className="space-y-6">
            <InboxHeader
                title={`${InboxTitle || 'Item Code Verification'} (${inbox.length})`}
                subtitle={ModuleDisplayName}
                itemCount={inbox.length}
                onBackClick={handleBackToInbox}
                HeaderIcon={Package}
                badgeText="Purchase Module"
                badgeCount={inbox.length}
                searchConfig={{
                    enabled: true,
                    placeholder: 'Search by item name, code, row ID...',
                    value: searchQuery,
                    onChange: (e) => setSearchQuery(e.target.value),
                }}
                enableViewToggle
            />

            <InboxSplitLayout
                isLeftPanelCollapsed={isLeftPanelCollapsed}
                onLeftPanelCollapseToggle={setIsLeftPanelCollapsed}
                isLeftPanelHovered={isLeftPanelHovered}
                onLeftPanelHoverChange={setIsLeftPanelHovered}
                left={{
                    items: filteredItems,
                    selectedItem: selectedItem,
                    onItemSelect: setSelectedItem,
                    renderItem: renderItemCard,
                    renderListItem: renderListItem,
                    renderCollapsedItem: renderCollapsedItem,
                    loading: loading.inbox,
                    error: errors.inbox,
                    onRefresh: handleRefresh,
                    config: {
                        title: 'Pending Verification',
                        icon: Clock,
                        emptyMessage: 'No item codes pending verification.',
                        itemKey: 'Rowid',
                        enableCollapse: true,
                        enableRefresh: true,
                        enableHover: true,
                        maxHeight: '100%',
                        headerGradient: 'from-blue-50 to-orange-50 dark:from-blue-900/20 dark:to-orange-900/20',
                    },
                    renderPopupContent: (_item) => renderDetailContent(true),
                    popupConfig: {
                        title: 'Item Code Verification',
                        icon: Package,
                        headerGradient: 'from-blue-50 to-orange-50 dark:from-blue-900/20 dark:to-orange-900/20',
                        maxWidth: 'max-w-[80vw]',
                    },
                }}
                right={{
                    selectedItem: selectedItem,
                    loading: loading.detail,
                    renderContent: renderDetailContent,
                    config: {
                        title: 'Item Code Verification',
                        icon: Package,
                        selectedTitle: 'Item Code Verification',
                        emptyTitle: 'No Item Code Selected',
                        emptyMessage: 'Select an item code from the list to review and verify.',
                        headerGradient: 'from-blue-50 to-orange-50 dark:from-blue-900/20 dark:to-orange-900/20',
                        maxHeight: 'calc(100vh - 200px)',
                        sticky: true,
                        stickyTop: '1.5rem',
                    },
                }}
            />
        </div>
    );
};

export default VerifyItemCode;
